/**
 * NyumbaSuite pricing — "start free, pay for the units you manage, get better
 * rates as your portfolio grows".
 *
 * The NUMBERS are not here. Rates, the free threshold, the minimum and the
 * annual discount are configuration and arrive from GET /api/v1/pricing, so a
 * published price can never contradict what the billing engine charges
 * (rule BR-21). What lives here is the ARITHMETIC — the one copy of it, used
 * by the server's billing engine, the web's calculator and the app alike.
 * Before Phase 0 of the mobile app this file existed twice, once in
 * src/domain/pricing.ts and once in web/src/lib/pricing.ts, guarded only by
 * the worked examples the server renders beside the calculator.
 */

export interface PricingTier {
  /** Inclusive upper bound of the band; null means it runs to infinity. */
  upTo: number | null;
  /** Per unit, per month. */
  rate: number;
  label: string | null;
}

export interface PricingModel {
  /** The package these bands price. Absent on the client's fallback model. */
  planCode?: string;
  tiers: PricingTier[];
  freeUnits: number;
  minimumMonthly: number;
  annualDiscountPercent: number;
  /** Which units attract the recurring charge. */
  billableBasis: 'occupied' | 'active';
  currencyCode: string;
}

export interface QuoteBand {
  from: number;
  to: number;
  units: number;
  rate: number;
  amount: number;
}

export interface Quote {
  units: number;
  free: boolean;
  bands: QuoteBand[];
  monthly: number;
  annualBeforeDiscount: number;
  annual: number;
  annualSaving: number;
  minimumApplied: boolean;
  effectiveRate: number | null;
}

export interface PricingPlan {
  code: string;
  name: string;
  description: string | null;
  audience: string | null;
  plan_kind: 'free' | 'metered' | 'custom';
  unit_min: number;
  unit_max: number | null;
  unit_limit: number | null;
  user_limit: number;
  sms_allowance: number;
  price_monthly: string;
  price_annual: string;
  currency_code: string;
  trial_days: number;
  cta_label: string | null;
  accent_color: string | null;
  highlights: string[];
  sort_order: number;
  /** The rate this package charges for its first paid unit. */
  fromRate: number | null;
  /** This package's OWN rate ladder and floor. Null on Free and Custom. */
  tiers: PricingTier[] | null;
  minimumMonthly: number | null;
  examples: Quote[] | null;
}

export interface PricingResponse {
  model: PricingModel;
  plans: PricingPlan[];
  examples: Quote[];
}

/** Cents, not shillings: a rate may carry two decimals, and multiplying those
 *  by a few hundred units in floating point leaves rounding dust. */
function cents(n: number): number {
  return Math.round(n * 100);
}

/**
 * Progressive per-unit pricing. Each unit is charged at the rate of the band
 * it falls into — never the whole portfolio at the rate of the highest band it
 * reaches. That is what makes the 100th unit cost the same whether the
 * portfolio has 100 units or 600, and why adding a unit can never jump the
 * bill (BR-20).
 */
export function quoteUnits(model: PricingModel, unitsRaw: number): Quote {
  const units = Math.max(0, Math.floor(unitsRaw));

  if (units <= model.freeUnits) {
    return {
      units, free: true, bands: [], monthly: 0,
      annualBeforeDiscount: 0, annual: 0, annualSaving: 0,
      minimumApplied: false, effectiveRate: 0,
    };
  }

  // Past the free threshold every unit is charged, starting from the first —
  // the threshold is a plan boundary, not an allowance carried forward.
  const bands: QuoteBand[] = [];
  let total = 0;
  let floor = 0;

  for (const tier of model.tiers) {
    if (floor >= units) break;
    const ceiling = tier.upTo === null ? units : Math.min(tier.upTo, units);
    const take = ceiling - floor;
    if (take > 0) {
      const amount = take * cents(tier.rate);
      total += amount;
      bands.push({ from: floor + 1, to: ceiling, units: take, rate: tier.rate, amount: amount / 100 });
    }
    floor = tier.upTo === null ? units : tier.upTo;
  }

  const minimum = cents(model.minimumMonthly);
  const minimumApplied = total < minimum;
  const monthly = (minimumApplied ? minimum : total) / 100;

  // Annual is the progressive monthly figure discounted, never a second matrix.
  const annualBeforeDiscount = monthly * 12;
  const annual = Math.round(annualBeforeDiscount * (1 - model.annualDiscountPercent / 100));

  return {
    units, free: false, bands, monthly,
    annualBeforeDiscount, annual,
    annualSaving: annualBeforeDiscount - annual,
    minimumApplied,
    effectiveRate: units > 0 ? Math.round((monthly / units) * 100) / 100 : null,
  };
}

/**
 * Shown for the instant before the live model arrives, and if it never does.
 * Mirrors the current published rate card so the first paint matches; the API
 * (the configured rate matrix) is always authoritative and replaces it.
 *
 * BILLABLE BASIS IS 'active', AND SAYING 'occupied' WAS A PROMISE WE DO NOT
 * KEEP. It is not a dormant field: the landing page's small print reads it and
 * prints one of two sentences. With 'occupied' a visitor was told "a vacant
 * unit attracts no recurring charge" - on first paint, and for as long as
 * /api/v1/pricing was unreachable - while every environment is configured
 * 'active' and bills every unit under management, vacant included. The
 * generous version is the one that was showing, which is the worse way round.
 *
 * Every figure here is checked against the live pricing_settings and the
 * standard ladder by a test, so this cannot drift again in silence.
 */
export const FALLBACK_MODEL: PricingModel = {
  tiers: [
    { upTo: 100, rate: 150, label: null },
    { upTo: 500, rate: 120, label: null },
    { upTo: null, rate: 100, label: null },
  ],
  freeUnits: 3,
  minimumMonthly: 0,
  annualDiscountPercent: 16.6667,
  billableBasis: 'active',
  currencyCode: 'KES',
};

export function formatKES(n: number): string {
  return 'KES ' + Math.round(n).toLocaleString('en-KE');
}

/** "0-3 units", "4-100 units", "501+ units". */
export function bandLabel(min: number, max: number | null): string {
  return max === null ? `${min}+ units` : `${min}-${max} units`;
}

/** The rate matrix as rows, for the "how the price is built" table. */
export function rateRows(model: PricingModel): { range: string; rate: string }[] {
  const rows = [{
    range: bandLabel(0, model.freeUnits),
    rate: 'Free',
  }];
  let floor = model.freeUnits;
  for (const t of model.tiers) {
    if (t.upTo !== null && t.upTo <= floor) continue;
    rows.push({
      range: t.upTo === null ? `${floor + 1}+ units` : `${floor + 1}-${t.upTo} units`,
      rate: `${formatKES(t.rate)} / unit`,
    });
    if (t.upTo === null) break;
    floor = t.upTo;
  }
  return rows;
}

/**
 * One package's own ladder, for its card: the same arithmetic as rateRows
 * without the free row, starting at the package's own first paid unit.
 */
export function planLadder(
  tiers: PricingTier[] | null, startsAt: number,
): { range: string; rate: string }[] {
  if (!tiers?.length) return [];
  const rows: { range: string; rate: string }[] = [];
  let floor = Math.max(0, startsAt - 1);
  for (const t of tiers) {
    if (t.upTo !== null && t.upTo <= floor) continue;
    rows.push({
      range: t.upTo === null ? `${floor + 1}+` : `${floor + 1}-${t.upTo}`,
      rate: formatKES(t.rate),
    });
    if (t.upTo === null) break;
    floor = t.upTo;
  }
  return rows;
}

/**
 * What the bands alone come to, before any floor. Not the same as the total
 * when a package's minimum applies — 20 units on Enterprise is 20 × 250 =
 * 5,000 and a bill of 15,000 — so the working is never printed as if it were.
 */
export function bandTotal(q: Quote): number {
  return q.bands.reduce((t, b) => t + b.units * b.rate, 0);
}

/** "20 × 100" — the sum behind a quote, written the way the doc writes it. */
export function bandSum(q: Quote): string {
  return q.bands.map(b => `${b.units} × ${b.rate}`).join(' + ');
}
